import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Globe } from 'lucide-react';
import { useUserPreferences } from '../../hooks/useUserPreferences';

interface GroupSelectorProps {
  onLoadGroup?: (groupId: string) => void;
  className?: string;
  hasChanges?: boolean;
}

export function GroupSelector({ onLoadGroup, className = '', hasChanges = false }: GroupSelectorProps) {
  const { t } = useTranslation();
  const { getPresets, getActiveGroup, loadPreset, isAuthenticated } = useUserPreferences();
  const [isOpen, setIsOpen] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState<any[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const activeGroup = groups.find(g => g.id === activeGroupId);

  // Load groups and active group
  const loadGroupData = async () => {
    try {
      // Only load data if user is authenticated
      if (isAuthenticated) {
        setLoadError(null);
        const groupsData = await getPresets();
        setGroups(groupsData);

        // Get active group ID from localStorage
        const activeId = await getActiveGroup();
        setActiveGroupId(activeId);
      } else {
        // Clear data for non-authenticated users
        setGroups([]);
        setActiveGroupId(null);
      }
    } catch (error) {
      console.error('Error loading group data:', error);
      // Keep the fetch failure distinct from "user has no groups"
      setLoadError(error instanceof Error ? error.message : String(error));
      setGroups([]);
      setActiveGroupId(null);
    }
  };

  useEffect(() => {
    loadGroupData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, getPresets, getActiveGroup]);

  // Listen for custom events when active group changes or zones are modified
  useEffect(() => {
    const handleActiveGroupChange = (event: CustomEvent) => {
      setActiveGroupId(event.detail.activeGroupId);
    };

    const handleZonesChanged = async () => {
      // Reload groups when zones are added/updated/deleted
      if (isAuthenticated) {
        try {
          const groupsData = await getPresets();
          setGroups(groupsData);
          setLoadError(null);
        } catch (error) {
          console.error('Error reloading groups after change:', error);
          setLoadError(error instanceof Error ? error.message : String(error));
        }
      }
    };

    window.addEventListener('activeGroupChanged', handleActiveGroupChange as EventListener);
    window.addEventListener('zonesChanged', handleZonesChanged);
    return () => {
      window.removeEventListener('activeGroupChanged', handleActiveGroupChange as EventListener);
      window.removeEventListener('zonesChanged', handleZonesChanged);
    };
  }, [isAuthenticated, getPresets]);


  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);


  const handleLoadGroup = async (groupId: string) => {
    try {
      setLoading(true);
      
      // Find the group data we already have
      const selectedGroup = groups.find(g => g.id === groupId);
      
      if (onLoadGroup) {
        onLoadGroup(groupId);
      } else {
        // Pass the group data directly to avoid another API call
        await loadPreset(groupId, selectedGroup);
      }
      setIsOpen(false);
      
      // Update local state to reflect the newly active group
      setActiveGroupId(groupId);
    } catch (error) {
      console.error('Error loading group:', error);
    } finally {
      setLoading(false);
    }
  };

  // Hide entirely when signed out — the global Sign In button handles auth.
  if (!isAuthenticated) return null;

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          if (!loading) setIsOpen(!isOpen);
        }}
        className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-md transition-colors ${
          hasChanges
            ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border border-amber-300 dark:border-amber-600'
            : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
        }`}
        disabled={loading}
      >
        <Globe className="w-4 h-4" />
        <span>
          {activeGroup ? activeGroup.name : t('groupSelector.selectZone')}
          {hasChanges && <span className="ml-1 text-xs">•</span>}
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && isAuthenticated && (
        <div
          className="absolute top-full mt-2 right-0 w-64 rounded-lg shadow-lg py-2 z-50"
          style={{
            background: 'var(--wv-card)',
            border: '1px solid var(--wv-card-border)',
          }}
        >
          <div className="max-h-80 overflow-y-auto">
            {loadError ? (
              <div className="px-4 py-3 text-sm text-center">
                <div style={{ color: 'var(--wv-text-muted)' }}>{t('zones.loadError')}</div>
                <button
                  onClick={() => loadGroupData()}
                  className="mt-2 px-3 py-1 text-sm rounded-md bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  style={{ color: 'var(--wv-text)' }}
                >
                  {t('zones.retry')}
                </button>
              </div>
            ) : groups.length === 0 ? (
              <div className="px-4 py-3 text-sm text-center" style={{ color: 'var(--wv-text-muted)' }}>
                {loading ? t('zones.loading') : t('zones.noneAvailable')}
              </div>
            ) : (
              groups.map((group, index) => (
                <button
                  key={group.id}
                  onClick={() => handleLoadGroup(group.id)}
                  className="w-full flex items-start gap-2 px-4 py-2 text-sm text-left transition-colors group-selector-item"
                  style={{
                    background: activeGroupId === group.id ? 'var(--wv-accent-soft)' : 'transparent',
                  }}
                >
                  <div className="flex-1">
                    <div className="font-medium" style={{ color: 'var(--wv-text)' }}>
                      {group.name}
                      {group.isDefault && (
                        <span className="ml-2 text-xs" style={{ color: 'var(--wv-text-muted)' }}>
                          {t('zones.defaultSuffix')}
                        </span>
                      )}
                    </div>
                  </div>
                  {index < 9 && (
                    <div className="text-xs font-mono" style={{ color: 'var(--wv-text-faint)' }}>
                      {navigator.platform.includes('Mac') ? '⌘' : 'Ctrl+'}{index + 1}
                    </div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}